import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import { isPendingStatus as isPending } from '../components/admissions/admissionText';

// Applicants accepted on forms.powerlearnprojectafrica.org and what
// happened when user_mngt admitted them to their LMS cohort
// (docs/api/forms-admissions.md in lms_backend).
export const ADMISSIONS_PATH = '/admissions';
const KEY = ['admissions'];
export const PAGE_SIZE = 50;
const MAX_BATCH = 200;

// Drop empty filters so the query key and the request stay tidy.
const clean = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

export const useAdmissionsSummary = (
  { cohortId, from, to } = {},
  options = {}
) => {
  const params = clean({ cohort_id: cohortId, from, to });
  return useQuery(
    [...KEY, 'summary', params],
    async () =>
      (await axiosInstancev2.get(`${ADMISSIONS_PATH}/summary`, { params }))
        .data,
    {
      // Keep the filter bar on screen while counts for new filters load.
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 30 * 1000,
      ...options,
    }
  );
};

export const listParams = (filters) =>
  clean({
    cohort_id: filters.cohortId,
    status: filters.status,
    reason: filters.reason,
    q: filters.q,
    from: filters.from,
    to: filters.to,
    // The server defaults to the latest record per submission.
    latest: filters.allAttempts ? 'false' : undefined,
  });

export const useAdmissions = (filters, offset = 0) => {
  const params = { ...listParams(filters), limit: PAGE_SIZE, offset };
  return useQuery(
    [...KEY, 'list', params],
    async () => (await axiosInstancev2.get(ADMISSIONS_PATH, { params })).data,
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
      // Keep rows moving while any of them is still being admitted.
      refetchInterval: (data) =>
        data?.admissions?.some((a) => isPending(a.status)) ? 10000 : false,
    }
  );
};

export const useAdmission = (id) =>
  useQuery(
    [...KEY, 'one', id],
    async () => (await axiosInstancev2.get(`${ADMISSIONS_PATH}/${id}`)).data,
    {
      enabled: Boolean(id),
      refetchOnWindowFocus: false,
      retry: false,
      refetchInterval: (data) =>
        isPending(data?.admission?.status) ? 5000 : false,
    }
  );

export const useReplay = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (id) =>
      (await axiosInstancev2.post(`${ADMISSIONS_PATH}/${id}/replay`)).data,
    { onSettled: () => queryClient.invalidateQueries(KEY) }
  );
};

// Every replayable admission matching the filters, fetched page by page.
const replayableIds = async (filters) => {
  const ids = [];
  let offset = 0;
  let total = Infinity;
  while (offset < total) {
    // eslint-disable-next-line no-await-in-loop
    const { data } = await axiosInstancev2.get(ADMISSIONS_PATH, {
      params: { ...listParams(filters), limit: MAX_BATCH, offset },
    });
    const rows = data?.admissions ?? [];
    total = data?.total ?? 0;
    rows.filter((a) => a.can_replay).forEach((a) => ids.push(a.id));
    if (!rows.length) break;
    offset += rows.length;
  }
  return ids;
};

// "Replay all" for one failure reason: the server takes 200 ids per call.
export const useBulkReplay = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (filters) => {
      const ids = await replayableIds(filters);
      const result = { replayed: [], skipped: [] };
      for (let i = 0; i < ids.length; i += MAX_BATCH) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await axiosInstancev2.post(
          `${ADMISSIONS_PATH}/replay`,
          { ids: ids.slice(i, i + MAX_BATCH) }
        );
        result.replayed.push(...(data?.replayed ?? []));
        result.skipped.push(...(data?.skipped ?? []));
      }
      return result;
    },
    { onSettled: () => queryClient.invalidateQueries(KEY) }
  );
};

export const admissionError = (error, fallback) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.error;
  if (status === 404 && !message) {
    return "Admissions from forms aren't available on the server yet.";
  }
  if (status === 503) {
    return message || 'Admissions from forms are switched off on the server.';
  }
  if (status === 403) {
    return message || 'You need the ManageAdmissions permission.';
  }
  return message || fallback;
};
